"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

interface RouteLeaveGuardProps {
  when: boolean;
  message?: string;
}

export function RouteLeaveGuard({
  when,
  message = "You have unsaved changes. Are you sure you want to leave?",
}: RouteLeaveGuardProps) {
  const router = useRouter();
  const pathname = usePathname();

  const lastApproved = useRef(pathname);

  useEffect(() => {
    if (!when) {
      lastApproved.current = pathname;
      return;
    }

    if (pathname !== lastApproved.current) {
      const ok = window.confirm(message);
      if (!ok) {
        router.back();
      } else {
        lastApproved.current = pathname;
      }
    }
  }, [when, message, pathname, router]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!when) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [when]);

  return null;
}
